import { CategoryQuestions } from '../types';

export const relationshipDecisionsQuestions: CategoryQuestions = {
  title: 'Personalize Your Relationship Decision Session',
  questions: {
    preferredStyle: {
      label: 'What approach feels most supportive for you right now?',
      type: 'radio',
      options: ['Quiet reflection and inner listening', 'Emotional processing and healing', 'Values clarification exercises', 'Self-compassion and gentleness', 'Wisdom and insight meditation', 'Present moment awareness']
    },
    primaryGoal: {
      label: 'What aspect of your relationship decision feels most important right now?',
      type: 'radio',
      options: ['Understanding my true feelings', 'Connecting with my values and priorities', 'Exploring what feels authentic to me', 'Finding inner peace with uncertainty', 'Trusting my inner wisdom', 'Processing emotions around the situation']
    },
    decisionStage: {
      label: 'Where are you in your decision-making process?',
      type: 'radio',
      options: ['Just beginning to consider options', 'Exploring different possibilities', 'Narrowing down choices', 'Seeking clarity on final decision', 'Need support implementing decision', 'Processing decision aftermath']
    },
    primaryConcern: {
      label: 'What additional aspect would you like to explore?',
      type: 'radio',
      options: ['Understanding my true feelings', 'Connecting with my values and priorities', 'Exploring what feels authentic to me', 'Finding inner peace with uncertainty', 'Trusting my inner wisdom', 'Processing emotions around the situation']
    },
    valuesExploration: {
      label: 'What values feel most important to honor in this decision?',
      type: 'radio',
      options: ['Love and connection', 'Personal growth and authenticity', 'Family and commitment', 'Independence and freedom', 'Safety and security', 'Honesty and integrity']
    },
    emotionalState: {
      label: 'How would you describe your current emotional state?',
      type: 'radio',
      options: ['Feeling confused or uncertain', 'Overwhelmed by options', 'Anxious about making the wrong choice', 'Hopeful but cautious', 'Clear but need support', 'Peaceful and trusting my process']
    },
    supportNeeded: {
      label: 'What kind of inner support would feel most helpful?',
      type: 'radio',
      options: ['Gentle self-compassion practices', 'Clarity and wisdom meditation', 'Emotional processing and release', 'Values alignment exploration', 'Trust in inner knowing', 'Peace with uncertainty']
    },
    sessionApproach: {
      label: 'What approach feels most supportive for you right now?',
      type: 'radio',
      options: ['Quiet reflection and inner listening', 'Emotional processing and healing', 'Values clarification exercises', 'Self-compassion and gentleness', 'Wisdom and insight meditation', 'Present moment awareness']
    },
    additionalNotes: {
      label: 'Is there anything else the AI should know to create the perfect session for you?',
      type: 'textarea',
      placeholder: 'Any additional context, special requests, or details that would make this session more meaningful...'
    },
  },
  groupTitle: 'Personalize Your Group Decision Support Session',
  groupQuestions: {
    preferredStyle: {
      label: 'What approach feels most supportive for your group?',
      type: 'radio',
      options: ['Quiet reflection together', 'Emotional processing as a group', 'Values clarification workshop', 'Self-compassion circle', 'Wisdom sharing session', 'Present moment mindfulness']
    },
    primaryGoal: {
      label: 'What would be most helpful for the group to explore together?',
      type: 'radio',
      options: ['Connecting with inner wisdom', 'Processing emotions around decisions', 'Exploring personal values and priorities', 'Practicing self-compassion', 'Finding peace with uncertainty', 'Supporting each other\'s decision process']
    },
    groupType: {
      label: 'What type of group decision support are you creating?',
      type: 'radio',
      options: ['Decision-making workshop', 'Relationship support group', 'Values exploration circle', 'Emotional processing group', 'Wisdom and clarity session', 'Self-compassion practice group']
    },
    groupFocus: {
      label: 'What additional focus would benefit the group?',
      type: 'radio',
      options: ['Connecting with inner wisdom', 'Processing emotions around decisions', 'Exploring personal values and priorities', 'Practicing self-compassion', 'Finding peace with uncertainty', 'Supporting each other\'s decision process']
    },
    experienceLevels: {
      label: 'What describes the decision-making experience in your group?',
      type: 'radio',
      options: ['All facing major relationship decisions', 'Mix of decision stages and types', 'Processing past relationship decisions', 'Learning decision-making skills', 'Supporting ongoing relationship choices', 'Healing from difficult relationship decisions']
    },
    additionalNotes: {
      label: 'Is there anything else the AI should know to create the perfect session for your group?',
      type: 'textarea',
      placeholder: 'Any additional context, special requests, or details that would make this session more meaningful...'
    },
  },
  practitionerGroupTitle: 'Personalize Your Clients\' Group Decision Support Session',
  practitionerGroupQuestions: {
    preferredStyle: {
      label: 'What approach feels most supportive for your clients?',
      type: 'radio',
      options: ['Quiet reflection together', 'Emotional processing as a group', 'Values clarification workshop', 'Self-compassion circle', 'Wisdom sharing session', 'Present moment mindfulness']
    },
    primaryGoal: {
      label: 'What would be most helpful for your clients to explore together?',
      type: 'radio',
      options: ['Connecting with inner wisdom', 'Processing emotions around decisions', 'Exploring personal values and priorities', 'Practicing self-compassion', 'Finding peace with uncertainty', 'Supporting each other\'s decision process']
    },
    groupType: {
      label: 'What type of group decision support are you creating for your clients?',
      type: 'radio',
      options: ['Decision-making workshop', 'Relationship support group', 'Values exploration circle', 'Emotional processing group', 'Wisdom and clarity session', 'Self-compassion practice group']
    },
    groupFocus: {
      label: 'What additional focus would benefit your clients?',
      type: 'radio',
      options: ['Connecting with inner wisdom', 'Processing emotions around decisions', 'Exploring personal values and priorities', 'Practicing self-compassion', 'Finding peace with uncertainty', 'Supporting each other\'s decision process']
    },
    experienceLevels: {
      label: 'What describes the decision-making experience in your clients?',
      type: 'radio',
      options: ['All facing major relationship decisions', 'Mix of decision stages and types', 'Processing past relationship decisions', 'Learning decision-making skills', 'Supporting ongoing relationship choices', 'Healing from difficult relationship decisions']
    },
    additionalNotes: {
      label: 'Is there anything else the AI should know to create the perfect session for your clients?',
      type: 'textarea',
      placeholder: 'Any additional context, special requests, or details that would make this session more meaningful...'
    },
  },
  practitionerTitle: 'Personalize Your Client\'s Relationship Decision Session',
  practitionerQuestions: {
    preferredStyle: {
      label: 'What approach feels most supportive for your client right now?',
      type: 'radio',
      options: ['Quiet reflection and inner listening', 'Emotional processing and healing', 'Values clarification exercises', 'Self-compassion and gentleness', 'Wisdom and insight meditation', 'Present moment awareness']
    },
    primaryGoal: {
      label: 'What aspect of your client\'s relationship decision feels most important right now?',
      type: 'radio',
      options: ['Understanding their true feelings', 'Connecting with their values and priorities', 'Exploring what feels authentic to them', 'Finding inner peace with uncertainty', 'Trusting their inner wisdom', 'Processing emotions around the situation']
    },
    decisionStage: {
      label: 'Where is your client in their decision-making process?',
      type: 'radio',
      options: ['Just beginning to consider options', 'Exploring different possibilities', 'Narrowing down choices', 'Seeking clarity on final decision', 'Need support implementing decision', 'Processing decision aftermath']
    },
    primaryConcern: {
      label: 'What additional aspect would your client like to explore?',
      type: 'radio',
      options: ['Understanding their true feelings', 'Connecting with their values and priorities', 'Exploring what feels authentic to them', 'Finding inner peace with uncertainty', 'Trusting their inner wisdom', 'Processing emotions around the situation']
    },
    valuesExploration: {
      label: 'What values feel most important for your client to honor in this decision?',
      type: 'radio',
      options: ['Love and connection', 'Personal growth and authenticity', 'Family and commitment', 'Independence and freedom', 'Safety and security', 'Honesty and integrity']
    },
    emotionalState: {
      label: 'How would you describe your client\'s current emotional state?',
      type: 'radio',
      options: ['Feeling confused or uncertain', 'Overwhelmed by options', 'Anxious about making the wrong choice', 'Hopeful but cautious', 'Clear but need support', 'Peaceful and trusting their process']
    },
    supportNeeded: {
      label: 'What kind of inner support would feel most helpful for your client?',
      type: 'radio',
      options: ['Gentle self-compassion practices', 'Clarity and wisdom meditation', 'Emotional processing and release', 'Values alignment exploration', 'Trust in inner knowing', 'Peace with uncertainty']
    },
    sessionApproach: {
      label: 'What approach feels most supportive for your client right now?',
      type: 'radio',
      options: ['Quiet reflection and inner listening', 'Emotional processing and healing', 'Values clarification exercises', 'Self-compassion and gentleness', 'Wisdom and insight meditation', 'Present moment awareness']
    },
    additionalNotes: {
      label: 'Is there anything else the AI should know to create the perfect session for your client?',
      type: 'textarea',
      placeholder: 'Any additional context, special requests, or details that would make this session more meaningful...'
    },
  },
  giftTitle: 'Personalize Decision Support for Your Loved One',
  giftQuestions: {
    preferredStyle: {
      label: 'What approach feels most supportive for them right now?',
      type: 'radio',
      options: ['Quiet reflection and inner listening', 'Emotional processing and healing', 'Values clarification exercises', 'Self-compassion and gentleness', 'Wisdom and insight meditation']
    },
    primaryGoal: {
      label: 'What aspect of their decision feels most important right now?',
      type: 'radio',
      options: ['Understanding their true feelings', 'Connecting with their values and priorities', 'Exploring what feels authentic to them', 'Finding inner peace with uncertainty', 'Trusting their inner wisdom']
    },
    decisionContext: {
      label: 'What decision are they working through? (Optional)',
      type: 'textarea',
      placeholder: 'Describe the relationship decision or situation they are navigating...'
    },
    emotionalState: {
      label: 'How would you describe their current emotional state?',
      type: 'radio',
      options: ['Feeling confused or uncertain', 'Overwhelmed by options', 'Anxious about making the wrong choice', 'Hopeful but cautious', 'Peaceful and trusting their process']
    },
    additionalNotes: {
      label: 'Anything else the AI should know to create a perfect session for them?',
      type: 'textarea',
      placeholder: 'Any additional context, special requests, or details that would make this session more meaningful...'
    },
  }
};